import { ApolloClient, NormalizedCacheObject } from '@apollo/client'
import { useMemo, useRef } from 'react'
import { Brand, BrandSortOrder } from 'types/graphql'
import { useCurrentUser } from 'modules/auth/useCurrentUser'
import { queryMyUnreviewedTestedBrands, useMyUnreviewedTestedBrands } from 'modules/reviews/form/queries/useMyUnreviewedTestedBrands'
import { queryBrandsDiscoveryWithClient, useBrandsDiscovery } from '../list/queries/getDiscoveryBrands'

const MAX_BRANDS = 30
const DISCOVER_SORT_ORDER = BrandSortOrder.MostReviewed
const getBrandDiscoveryFilters = (isLoggedIn: boolean) => (isLoggedIn ? ['current-user-unreviewed-brands'] : undefined)

export const preloadUnreviewedBrandsList = ({
  apolloClient,
  amountToReturn,
  isLoggedIn
}: {
  apolloClient: ApolloClient<NormalizedCacheObject>
  amountToReturn: number
  isLoggedIn: boolean
}) => {
  return Promise.all([
    queryBrandsDiscoveryWithClient(apolloClient, {
      take: amountToReturn,
      order: DISCOVER_SORT_ORDER,
      filters: getBrandDiscoveryFilters(isLoggedIn),
      skip: 0
    }),
    queryMyUnreviewedTestedBrands({ apolloClient, variables: { take: 30, skip: 0 } })
  ])
}

interface Options {
  amountToReturn?: number
  allowCache?: boolean
  addRandomness?: boolean
}

export const useUnreviewedBrands = (options?: Options) => {
  const { amountToReturn = MAX_BRANDS, allowCache = false, addRandomness = false } = options ?? {}
  const { isLoggedIn } = useCurrentUser()

  // Randomly skip some brands to avoid always showing the same brands first
  const skip = useRef(addRandomness ? Math.floor(Math.random() * 10) : 0)

  const {
    data: testedBrands,
    loading: loadingTested,
    called: testedCalled
  } = useMyUnreviewedTestedBrands({
    fetchPolicy: allowCache ? undefined : 'no-cache',
    skip: !isLoggedIn,
    variables: { take: amountToReturn, skip: skip.current, includeDismissed: false }
  })

  const {
    data: recommendedBrands,
    loading: loadingRecommended,
    called: recommendedCalled,
    refetch: refetchDiscovery
  } = useBrandsDiscovery({
    fetchPolicy: allowCache ? undefined : 'network-only',
    variables: {
      order: DISCOVER_SORT_ORDER,
      filters: getBrandDiscoveryFilters(isLoggedIn),
      take: amountToReturn,
      skip: skip.current,
      includeDismissed: false
    }
  })

  const unreviewedBrands = useMemo(() => {
    try {
      const tested = testedBrands?.list?.brands.items || []
      const recommended = recommendedBrands?.brands.items || []
      let brands = [...tested]

      if (tested.length < amountToReturn) {
        const recommendedLength = amountToReturn - tested.length
        const recommendedWithNoDuplicates = recommended
          .filter((brand) => !tested.find((testedBrand) => brand.id === testedBrand.id))
          .slice(0, recommendedLength) as Brand[]
        brands = [...tested, ...recommendedWithNoDuplicates]
      }

      return brands
    } catch {
      return null
    }
  }, [testedBrands, recommendedBrands, amountToReturn])

  return {
    unreviewedBrands,
    loading: loadingRecommended || loadingTested,
    called: testedCalled || recommendedCalled,
    refetchDiscovery
  }
}
